<template>
  <div id="contenedor-editar-contrato">
    <v-form v-model="formularioValido" ref="editarContrato">
      <v-row>
        <v-col cols="12" class="pa-1">
          <v-card flat>
            <v-card-text>
              <!-- Paso 1: Tipo y Origen del Contrato -->
              <v-row class="mb-2">
                <v-col cols="12">
                  <div class="subtitle-2 primary--text mb-2">
                    <v-icon small color="primary" class="mr-1">mdi-numeric-1-circle</v-icon>
                    Tipo y Origen del Contrato
                  </div>
                  <v-divider class="mb-3"></v-divider>
                </v-col>
                <v-col cols="12" md="4">
                  <v-switch
                    v-model="contrato.esObraFinanciada"
                    inset
                    color="success"
                    label="¿Es obra Financiada?"
                  />
                </v-col>
                <custom-autocomplete
                  cols="12"
                  md="8"
                  v-model="contrato.comoSeContrato"
                  label="¿Cómo se Contrató?"
                  :items="tiposContratos"
                  item-text="text"
                  item-value="id"
                  clearable
                  :rules="[mixReglas.requerido]"
                  @change="contrato.idLicitacion = null; contrato.idAdjudicacionDirecta = null"
                >
                  <template v-slot:prepend-inner>
                    <v-icon small>mdi-gavel</v-icon>
                  </template>
                </custom-autocomplete>
              </v-row>

              <!-- Paso 2: Licitación o Adjudicación (según tipo) -->
              <v-row v-if="contrato.comoSeContrato" class="mb-2">
                <v-col cols="12">
                  <div class="subtitle-2 primary--text mb-2">
                    <v-icon small color="primary" class="mr-1">mdi-numeric-2-circle</v-icon>
                    Selección de
                    {{ contrato.comoSeContrato === 'AD' ? 'Adjudicación Directa' : 'Licitación' }}
                  </div>
                  <v-divider class="mb-3"></v-divider>
                </v-col>
                <custom-autocomplete
                  v-if="contrato.comoSeContrato !== 'AD'"
                  cols="12"
                  v-model="contrato.idLicitacion"
                  label="Licitación"
                  :items="getLicitacionesSelect"
                  item-text="texto"
                  item-value="id"
                  clearable
                  hint="Seleccione la licitación asociada"
                  persistent-hint
                >
                  <template v-slot:prepend-inner>
                    <v-icon small>mdi-briefcase-search</v-icon>
                  </template>
                </custom-autocomplete>
                <custom-autocomplete
                  v-else
                  cols="12"
                  v-model="contrato.idAdjudicacionDirecta"
                  label="Adjudicación Directa"
                  :items="getAdjudicacionesDirectasSelect"
                  item-text="texto"
                  item-value="id"
                  clearable
                  hint="Los datos se cargarán automáticamente"
                  persistent-hint
                  @change="handleSetDatosContrato"
                >
                  <template v-slot:prepend-inner>
                    <v-icon small>mdi-briefcase-check</v-icon>
                  </template>
                </custom-autocomplete>
              </v-row>

              <!-- Paso 3: Datos básicos del contrato -->
              <v-row class="mb-2">
                <v-col cols="12">
                  <div class="subtitle-2 primary--text mb-2">
                    <v-icon small color="primary" class="mr-1">mdi-numeric-3-circle</v-icon>
                    Datos del Contrato
                  </div>
                  <v-divider class="mb-3"></v-divider>
                </v-col>
                <custom-text-field
                  cols="12"
                  md="4"
                  v-model="contrato.numero"
                  label="Número de Contrato"
                  :rules="[mixReglas.requerido]"
                >
                  <template v-slot:prepend-inner>
                    <v-icon small>mdi-pound</v-icon>
                  </template>
                </custom-text-field>
                <custom-date-picker
                  cols="12"
                  md="4"
                  v-model="contrato.fecha"
                  label="Fecha de Firma"
                  :rules="[mixReglas.requerido]"
                />
                <v-col cols="12" md="4">
                  <v-chip outlined color="info" class="mt-2">
                    <v-icon small left>mdi-calendar-range</v-icon>
                    Ejercicio: {{ contrato.ejercicio }}
                  </v-chip>
                </v-col>
                <custom-text-area
                  cols="12"
                  v-model="contrato.meta"
                  label="Objeto del Contrato"
                  rows="3"
                  :rules="[mixReglas.requerido]"
                  hint="Descripción detallada del objeto del contrato"
                  persistent-hint
                >
                  <template v-slot:prepend-inner>
                    <v-icon small>mdi-text-box</v-icon>
                  </template>
                </custom-text-area>
              </v-row>

              <!-- Paso 4: Monto del Contrato -->
              <v-row class="mt-4">
                <v-col cols="12">
                  <div class="subtitle-2 primary--text mb-2">
                    <v-icon small color="primary" class="mr-1">mdi-numeric-4-circle</v-icon>
                    Monto del Contrato
                  </div>
                  <v-divider class="mb-3"></v-divider>
                </v-col>
              </v-row>

              <!-- Obra Financiada - Desglose -->
              <template v-if="contrato.esObraFinanciada">
                <v-row>
                  <v-col cols="12">
                    <v-alert type="info" dense text border="left" class="mb-3">
                      <div class="caption">Obra con financiamiento - Desglose detallado</div>
                    </v-alert>
                  </v-col>
                </v-row>
                <v-row>
                  <v-col cols="12" md="3">
                    <div class="caption grey--text mb-1">COSTO DIRECTO</div>
                    <custom-text-field-green
                      v-model.number="contrato.importeCostoDirecto"
                      label="Importe"
                      es-dinero
                      prefix="$"
                      :rules="[mixReglas.requerido]"
                      @keyup="sumarImportesObrasFinanciada"
                    />
                  </v-col>
                  <v-col cols="12" md="3">
                    <div class="caption grey--text mb-1">COSTOS INDIRECTOS</div>
                    <v-row dense>
                      <custom-text-field-green
                        cols="5"
                        v-model.number="contrato.porcentajeCostoIndirectos"
                        label="%"
                        suffix="%"
                        :rules="[mixReglas.requerido]"
                      />
                      <custom-text-field-green
                        cols="7"
                        v-model.number="contrato.importeCostoIndirectos"
                        label="Importe"
                        es-dinero
                        prefix="$"
                        :rules="[mixReglas.requerido]"
                        @keyup="sumarImportesObrasFinanciada"
                      />
                    </v-row>
                  </v-col>
                  <v-col cols="12" md="3">
                    <div class="caption grey--text mb-1">UTILIDAD</div>
                    <v-row dense>
                      <custom-text-field-green
                        cols="5"
                        v-model.number="contrato.porcentajeUtilidad"
                        label="%"
                        suffix="%"
                        :rules="[mixReglas.requerido]"
                      />
                      <custom-text-field-green
                        cols="7"
                        v-model.number="contrato.importeUtilidad"
                        label="Importe"
                        es-dinero
                        prefix="$"
                        :rules="[mixReglas.requerido]"
                        @keyup="sumarImportesObrasFinanciada"
                      />
                    </v-row>
                  </v-col>
                  <v-col cols="12" md="3">
                    <div class="caption grey--text mb-1">CARGOS ADICIONALES</div>
                    <v-row dense>
                      <custom-text-field-green
                        cols="5"
                        v-model.number="contrato.porcentajeCargosAdicionales"
                        label="%"
                        suffix="%"
                        :rules="[mixReglas.requerido]"
                      />
                      <custom-text-field-green
                        cols="7"
                        v-model.number="contrato.importeCargosAdicionales"
                        label="Importe"
                        es-dinero
                        prefix="$"
                        :rules="[mixReglas.requerido]"
                        @keyup="sumarImportesObrasFinanciada"
                      />
                    </v-row>
                  </v-col>
                  <v-col cols="12" md="3">
                    <div class="caption grey--text mb-1">COSTO FINANCIERO</div>
                    <v-row dense>
                      <custom-text-field-green
                        cols="5"
                        v-model.number="contrato.porcentajeCostoFinanciero"
                        label="%"
                        suffix="%"
                        :rules="[mixReglas.requerido]"
                      />
                      <custom-text-field-green
                        cols="7"
                        v-model.number="contrato.importeCostoFinanciero"
                        label="Importe"
                        es-dinero
                        prefix="$"
                        :rules="[mixReglas.requerido]"
                        @keyup="sumarImportesObrasFinanciada"
                      />
                    </v-row>
                  </v-col>
                </v-row>
                <v-row>
                  <v-col cols="12">
                    <v-divider class="my-2"></v-divider>
                  </v-col>
                </v-row>
              </template>

              <!-- Subtotal, IVA y Total -->
              <v-row>
                <v-col cols="12" md="4">
                  <div class="subtitle-2 font-weight-bold mb-2">
                    {{ contrato.esObraFinanciada ? 'SUBTOTAL' : 'IMPORTE' }}
                  </div>
                  <custom-text-field-green
                    v-model.number="contrato.importe"
                    label="Importe del Contrato"
                    es-dinero
                    prefix="$"
                    :rules="[mixReglas.requerido, reglas.mayorCero]"
                    @keyup="calcularTotal"
                  />
                </v-col>
                <v-col cols="12" md="4">
                  <div class="caption grey--text mb-2">IMPUESTO AL VALOR AGREGADO</div>
                  <v-row dense>
                    <custom-text-field-green
                      cols="4"
                      v-model.number="contrato.porcentajeIva"
                      label="% IVA"
                      suffix="%"
                      es-dinero
                      :rules="[mixReglas.requerido]"
                      @keyup="calcularTotal"
                    />
                    <custom-text-field-green
                      cols="8"
                      v-model="contrato.iva"
                      label="Importe IVA"
                      prefix="$"
                      es-dinero
                      readonly
                      disabled
                    />
                  </v-row>
                </v-col>
                <v-col cols="12" md="4">
                  <div class="success lighten-5 pa-3 rounded">
                    <div class="subtitle-2 font-weight-bold success--text text--darken-2 mb-2">
                      TOTAL DEL CONTRATO
                    </div>
                    <custom-text-field-green
                      v-model="contrato.total"
                      es-dinero
                      label="Total"
                      prefix="$"
                      readonly
                      disabled
                    />
                  </div>
                </v-col>
              </v-row>

              <!-- Paso 5: Contratista -->
              <v-row class="mb-2">
                <v-col cols="12">
                  <div class="subtitle-2 primary--text mb-2">
                    <v-icon small color="primary" class="mr-1">mdi-numeric-5-circle</v-icon>
                    Contratista
                  </div>
                  <v-divider class="mb-3"></v-divider>
                </v-col>
                <custom-autocomplete
                  cols="12"
                  md="11"
                  v-model="contrato.idContratista"
                  label="Seleccionar Contratista"
                  :items="contratistas"
                  item-text="nombreRazonSocial"
                  item-value="id"
                  clearable
                  :rules="[mixReglas.requerido]"
                >
                  <template v-slot:prepend-inner>
                    <v-icon small>mdi-domain</v-icon>
                  </template>
                </custom-autocomplete>
                <v-col cols="1" class="d-flex align-center">
                  <v-tooltip bottom>
                    <template v-slot:activator="{ on, attrs }">
                      <registrar-nuevo-contratista
                        ref="registrarNuevoContratista"
                        only-icon
                        v-bind="attrs"
                        v-on="on"
                      />
                    </template>
                    <span>Agregar nuevo contratista</span>
                  </v-tooltip>
                </v-col>
              </v-row>

              <!-- Paso 6: Vigencia -->
              <v-row class="mb-2">
                <v-col cols="12">
                  <div class="subtitle-2 primary--text mb-2">
                    <v-icon small color="primary" class="mr-1">mdi-numeric-6-circle</v-icon>
                    Vigencia del Contrato
                  </div>
                  <v-divider class="mb-3"></v-divider>
                </v-col>
                <custom-date-picker
                  cols="12"
                  md="4"
                  v-model="contrato.fechaInicio"
                  label="Fecha de Inicio"
                  :rules="[mixReglas.requerido]"
                />
                <custom-date-picker
                  cols="12"
                  md="4"
                  v-model="contrato.fechaTermino"
                  label="Fecha de Término"
                  :rules="[mixReglas.requerido]"
                />
                <custom-text-field
                  v-model.number="contrato.plazo"
                  cols="12"
                  md="4"
                  readonly
                  label="Plazo Calculado"
                  suffix="días naturales"
                >
                  <template v-slot:prepend-inner>
                    <v-icon small>mdi-clock-outline</v-icon>
                  </template>
                </custom-text-field>
              </v-row>

              <!-- Paso 7: Obras asociadas -->
              <v-row class="mb-2">
                <v-col cols="12">
                  <div class="subtitle-2 primary--text mb-2">
                    <v-icon small color="primary" class="mr-1">mdi-numeric-7-circle</v-icon>
                    Obras Asociadas
                  </div>
                  <v-divider class="mb-3"></v-divider>
                </v-col>
                <v-col cols="12" md="6">
                  <v-switch
                    v-model="seleccionarObras"
                    @change="cambioCapturaObras"
                    inset
                    color="success"
                    label="¿Seleccionar obras existentes?"
                  />
                </v-col>
              </v-row>

              <!-- Seleccionador de obras -->
              <template v-if="seleccionarObras">
                <v-row>
                  <v-col cols="12">
                    <v-alert type="info" dense outlined border="left" class="mb-3">
                      Seleccione las obras que formarán parte de este contrato
                    </v-alert>
                    <obras-picker
                      :obrasContratos="obrasContratos"
                      :value="obrasPickerSelection"
                      para="CONTRATAR"
                      @input="handleSeleccionObras"
                    />
                  </v-col>
                </v-row>
              </template>

              <!-- Captura de obra desde contrato -->
              <template v-else>
                <v-row>
                  <v-col cols="12">
                    <v-alert type="info" dense outlined border="left" class="mb-3">
                      Capture los datos de la nueva obra
                    </v-alert>
                    <agregar-obra desde-contrato :obra="obra" />
                  </v-col>
                </v-row>
              </template>

              <!-- Lista de obras seleccionadas con importes -->
              <v-row v-if="obras.length > 0">
                <v-col cols="12">
                  <v-card outlined>
                    <v-card-subtitle class="font-weight-bold">
                      <v-icon small class="mr-1">mdi-format-list-bulleted</v-icon>
                      Obras en el Contrato ({{ obras.length }})
                    </v-card-subtitle>
                    <v-divider></v-divider>
                    <v-list dense>
                      <v-list-item
                        v-for="(obra, index) in obras"
                        :key="`obra-${obra.id}`"
                        :class="index % 2 === 0 ? 'grey lighten-5' : ''"
                      >
                        <v-list-item-avatar>
                          <v-chip small color="primary">
                            {{ index + 1 }}
                          </v-chip>
                        </v-list-item-avatar>
                        <v-list-item-content>
                          <v-list-item-title class="font-weight-bold">
                            {{ obra.clave }}
                          </v-list-item-title>
                          <v-list-item-subtitle class="text-caption">
                            {{ obra.meta }}
                          </v-list-item-subtitle>
                        </v-list-item-content>
                        <v-list-item-action style="min-width: 200px;">
                          <custom-text-field-green
                            v-model.number="obra.importe"
                            label="Importe"
                            prefix="$"
                            :value="0"
                            type="number"
                            dense
                            @keyup="sumarImportesObras"
                          />
                        </v-list-item-action>
                      </v-list-item>
                    </v-list>
                  </v-card>
                </v-col>
                <v-col v-if="obras.length > 1" cols="12">
                  <v-checkbox
                    v-model="contrato.seEjecutaraPorSeparado"
                    hide-details
                    color="secondary"
                    class="mt-0"
                  >
                    <template v-slot:label>
                      <span class="secondary--text font-weight-medium">
                        <v-icon small left color="secondary">mdi-source-branch</v-icon>
                        Las obras se ejecutarán por separado
                      </span>
                    </template>
                  </v-checkbox>
                </v-col>
              </v-row>

              <!-- Paso 8: Penalizaciones y Retenciones -->
              <v-row class="mb-2">
                <v-col cols="12">
                  <div class="subtitle-2 primary--text mb-2">
                    <v-icon small color="primary" class="mr-1">mdi-numeric-8-circle</v-icon>
                    Penalizaciones y Retenciones
                  </div>
                  <v-divider class="mb-3"></v-divider>
                </v-col>
              </v-row>
              <v-row>
                <v-col cols="12" md="6">
                  <v-card outlined class="pa-3">
                    <div class="d-flex align-center mb-3">
                      <v-icon color="error" class="mr-2">mdi-alert-circle</v-icon>
                      <span class="subtitle-1 font-weight-bold">Sanción por Atraso</span>
                    </div>
                    <v-radio-group
                      v-model="contrato.periodicidadSancion"
                      label="Periodicidad"
                      mandatory
                      hide-details
                      class="mt-0"
                    >
                      <v-radio value="diaria">
                        <template v-slot:label>
                          <div class="d-flex align-center">
                            <v-icon small left>mdi-calendar-today</v-icon>
                            <strong class="secondary--text">Diaria</strong>
                          </div>
                        </template>
                      </v-radio>
                      <v-radio value="mensual">
                        <template v-slot:label>
                          <div class="d-flex align-center">
                            <v-icon small left>mdi-calendar-month</v-icon>
                            <strong class="secondary--text">Mensual</strong>
                          </div>
                        </template>
                      </v-radio>
                    </v-radio-group>
                    <custom-text-field
                      v-model.number="contrato.porcentajeSancion"
                      label="Porcentaje de Sanción"
                      suffix="%"
                      type="number"
                      class="mt-3"
                    >
                      <template v-slot:prepend-inner>
                        <v-icon small>mdi-percent</v-icon>
                      </template>
                    </custom-text-field>
                  </v-card>
                </v-col>
                <v-col cols="12" md="6">
                  <v-card outlined class="pa-3">
                    <div class="d-flex align-center mb-3">
                      <v-icon color="warning" class="mr-2">mdi-cash-lock</v-icon>
                      <span class="subtitle-1 font-weight-bold">Retención</span>
                    </div>
                    <v-radio-group
                      v-model="contrato.tipoRetencion"
                      label="Base de Cálculo"
                      mandatory
                      hide-details
                      class="mt-0"
                    >
                      <v-radio value="baseDias">
                        <template v-slot:label>
                          <div class="d-flex align-center">
                            <v-icon small left>mdi-calendar-clock</v-icon>
                            <strong class="secondary--text">Por días de atraso</strong>
                          </div>
                        </template>
                      </v-radio>
                      <v-radio value="baseMonto">
                        <template v-slot:label>
                          <div class="d-flex align-center">
                            <v-icon small left>mdi-cash-multiple</v-icon>
                            <strong class="secondary--text">Por monto de atraso</strong>
                          </div>
                        </template>
                      </v-radio>
                    </v-radio-group>
                    <custom-text-field
                      v-model.number="contrato.porcentajeRetencion"
                      label="Porcentaje de Retención"
                      suffix="%"
                      type="number"
                      class="mt-3"
                    >
                      <template v-slot:prepend-inner>
                        <v-icon small>mdi-percent</v-icon>
                      </template>
                    </custom-text-field>
                  </v-card>
                </v-col>
              </v-row>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>
    </v-form>
  </div>
</template>

<script>
import LICITACINES_LISTAS_PARA_CONTRATAR from '@/graphql/querys/administracion/licitaciones/licitacionesListasParaContratar.gql';
import ADJUDICACIONES_DIRECTAS_LISTAS_PARA_CONTRATAR from '@/graphql/querys/administracion/adjudicaciones/adjudicacionesDirectasListasParaContratar.gql';
import RegistrarNuevoContratista from '@/components/administracion/configuracion/catalogos/contratistas/Nuevo.vue';
import AgregarObra from '@/components/administracion/obras/basico/Nueva.vue';
import CustomAutocomplete from '@/components/inputs/Autocomplete.vue';
import CustomTextField from '@/components/inputs/TextField.vue';
import CustomTextFieldGreen from '@/components/inputs/TextFieldGreen.vue';
import CustomTextArea from '@/components/inputs/TextArea.vue';
import CustomDatePicker from '@/components/inputs/DatePicker.vue';
import ObrasPicker from '@/components/ObrasPicker.vue';
import mixContratos from './mixContratos';

export default {
  name: 'EditarContrato',
  mixins: [mixContratos],
  props: {
    contratoEditar: {
      type: Object,
      required: true,
    },
    obrasContratos: {
      type: Array,
      required: false,
      default() {
        return [];
      },
    },
  },
  components: {
    RegistrarNuevoContratista,
    AgregarObra,
    CustomAutocomplete,
    CustomTextField,
    CustomTextFieldGreen,
    CustomTextArea,
    CustomDatePicker,
    ObrasPicker,
  },
  data() {
    return {
      seleccionarObras: this.contratoEditar.obras.length > 0,
      mostrarVentanaRegistrarNuevoContratista: false,
      contratistaSelected: null,
      formularioValido: false,
      enviandoFormulario: false,
      reglas: {
        mayorCero: (val) => {
          const valor = Number(String(val).replace(/\D/g, ''));
          return valor > 0 || 'El monto debe de ser mayor a 0.';
        },
      },
      contrato: {
        idContratista: this.contratoEditar.idContratista,
        idLicitacion: this.contratoEditar.idLicitacion,
        idAdjudicacionDirecta: this.contratoEditar.idAdjudicacionDirecta,
        seEjecutaraPorSeparado: this.contratoEditar.seEjecutaraPorSeparado,
        esObraFinanciada: this.contratoEditar.esObraFinanciada,
        comoSeContrato: this.contratoEditar.comoSeContrato,
        numero: this.contratoEditar.numero,
        ejercicio: this.contratoEditar.ejercicio,
        periodo: this.contratoEditar.periodo || 2,
        fecha: this.contratoEditar.fecha,
        meta: this.contratoEditar.meta,
        importeCostoDirecto: this.contratoEditar.importeCostoDirecto,
        porcentajeCostoIndirectos: this.contratoEditar.porcentajeCostoIndirectos,
        importeCostoIndirectos: this.contratoEditar.importeCostoIndirectos,
        porcentajeUtilidad: this.contratoEditar.porcentajeUtilidad,
        importeUtilidad: this.contratoEditar.importeUtilidad,
        porcentajeCargosAdicionales: this.contratoEditar.porcentajeCargosAdicionales,
        importeCargosAdicionales: this.contratoEditar.importeCargosAdicionales,
        porcentajeCostoFinanciero: this.contratoEditar.porcentajeCostoFinanciero,
        importeCostoFinanciero: this.contratoEditar.importeCostoFinanciero,
        importe: this.contratoEditar.importe,
        porcentajeIva: this.contratoEditar.porcentajeIva,
        iva: this.contratoEditar.iva,
        total: this.contratoEditar.total,
        fechaInicio: this.contratoEditar.fechaInicio,
        fechaTermino: this.contratoEditar.fechaTermino,
        plazo: this.contratoEditar.plazo,
        periodicidadSancion: this.contratoEditar.periodicidadSancion,
        porcentajeSancion: this.contratoEditar.porcentajeSancion,
        tipoRetencion: this.contratoEditar.tipoRetencion || 'baseDias',
        porcentajeRetencion: this.contratoEditar.porcentajeRetencion || 0.5,
        normatividad: this.contratoEditar.normatividad,
        obras: [],
      },
      obra: {
        idPrograma: '',
        idTipo: '',
        idSubtipo: '',
        ejercicio: 0,
        clave: '',
        meta: '',
        ubicacion: {
          idMunicipio: 'bb1d2835-b403-41dc-b9f3-dcb9b2489c89',
          ubicacion: '',
          localidad: '',
          cabeceraMunicipial: false,
        },
        impacto: {
          beneficiados: 0,
          metaFisica: 0,
          metaFisicaUnidad: '',
          justificacionTecnica: '',
          justificacionSocial: '',
          problematica: '',
          avanceFisicoActual: 0,
          avanceProgramadoCierre: 0,
          descripcionTotalObra: '',
          conceptosEjecutar: '',
        },
      },
      obras: [],
      tiposContratos: [
        { id: 'LP', text: 'LICITACIÓN PÚBLICA' },
        { id: 'LI', text: 'LICITACIÓN RESTINGIDA POR INVITACIÓN' },
        { id: 'AD', text: 'ADJUDICACION DIRECTA' },
      ],
      ejercicios: [],
    };
  },
  apollo: {
    licitacionesListasParaContratar: {
      query: LICITACINES_LISTAS_PARA_CONTRATAR,
      variables() {
        return {
          filtros: {
            tipo: this.contrato.comoSeContrato === 'LP' ? 1 : 2,
          },
        };
      },
      skip() {
        return this.contrato.comoSeContrato !== 'LP' && this.contrato.comoSeContrato !== 'LI';
      },
    },
    adjudicacionesDirectasListasParaContratar: {
      query: ADJUDICACIONES_DIRECTAS_LISTAS_PARA_CONTRATAR,
      skip() {
        return this.contrato.comoSeContrato !== 'AD';
      },
    },
  },
  mounted() {
    this.fetch(this.contratoEditar);
    this.obra.clave = this.contratoEditar.numero;
    this.obra.ejercicio = this.contratoEditar.ejercicio;
    this.obra.meta = this.contratoEditar.meta;
  },
  methods: {
    normalizarObrasContrato(obras = []) {
      return obras.map((obra) => ({
        id: obra.id,
        idObra: obra.idObra || obra.obra?.id || obra.id,
        clave: obra.obra?.clave || obra.clave || '',
        meta: obra.obra?.meta || obra.meta || '',
        importe: obra.importe || 0,
        porcentajeIva: obra.porcentajeIva,
        iva: obra.iva,
        total: obra.total,
        esActualizacion: obra.esActualizacion !== false,
      }));
    },
    handleSeleccionObras(obrasSeleccionadas) {
      const obrasActuales = this.normalizarObrasContrato(this.obras);
      const obrasExistentes = obrasActuales.filter((obra) => obra.esActualizacion);
      const obrasSeleccionadasNormalizadas = obrasSeleccionadas.map((obraSeleccionada) => {
        const obraExistente = obrasActuales.find(({ idObra }) => idObra === obraSeleccionada.id);

        if (obraExistente) return obraExistente;

        return {
          id: obraSeleccionada.id,
          idObra: obraSeleccionada.id,
          clave: obraSeleccionada.clave,
          meta: obraSeleccionada.meta,
          importe: obraSeleccionada.importe || 0,
          esActualizacion: false,
        };
      });

      this.obras = [...obrasExistentes, ...obrasSeleccionadasNormalizadas]
        .filter(
          (obra, index, collection) => collection.findIndex(
            ({ idObra }) => idObra === obra.idObra,
          ) === index,
        );

      this.sumarImportesObras();
    },
    handleSetDatosContrato() {
      const adjudicacionDirecta = this.getAdjudicacionesDirectasListasParaContratar.find(
        (adjudicacion) => adjudicacion.id === this.contrato.idAdjudicacionDirecta,
      );

      if (adjudicacionDirecta) {
        this.contrato.numero = adjudicacionDirecta.numeroContrato;
        this.contrato.meta = adjudicacionDirecta.meta;
        this.contrato.idContratista = adjudicacionDirecta.idContratista;
        this.contrato.fechaInicio = adjudicacionDirecta.fechaInicio;
        this.contrato.fechaTermino = adjudicacionDirecta.fechaTermino;
        this.contrato.importe = adjudicacionDirecta.importe;
        this.contrato.porcentajeIva = adjudicacionDirecta.porcentajeIva;
        this.calcularTotal();
        return;
      }

      this.contrato.numero = null;
      this.contrato.meta = null;
      this.contrato.idContratista = null;
      this.contrato.fechaInicio = new Date().toISOString().substr(0, 10);
      this.contrato.fechaTermino = new Date().toISOString().substr(0, 10);
      this.contrato.importe = 0;
      this.contrato.porcentajeIva = 16;
      this.calcularTotal();
    },
    cambioCapturaObras() {
      if (!this.seleccionarObras) {
        this.obras = [];
      }
    },
    fetch(contrato) {
      this.contrato.idContratista = contrato.idContratista;
      this.contrato.idLicitacion = contrato.idLicitacion;
      this.contrato.idAdjudicacionDirecta = contrato.idAdjudicacionDirecta;
      this.contrato.seEjecutaraPorSeparado = contrato.seEjecutaraPorSeparado;
      this.contrato.esObraFinanciada = contrato.esObraFinanciada;
      this.contrato.comoSeContrato = contrato.comoSeContrato;
      this.contrato.numero = contrato.numero;
      this.contrato.ejercicio = contrato.ejercicio;
      this.contrato.fecha = contrato.fecha;
      this.contrato.meta = contrato.meta;
      this.contrato.importeCostoDirecto = contrato.importeCostoDirecto;
      this.contrato.porcentajeCostoIndirectos = contrato.porcentajeCostoIndirectos;
      this.contrato.importeCostoIndirectos = contrato.importeCostoIndirectos;
      this.contrato.porcentajeUtilidad = contrato.porcentajeUtilidad;
      this.contrato.importeUtilidad = contrato.importeUtilidad;
      this.contrato.porcentajeCargosAdicionales = contrato.porcentajeCargosAdicionales;
      this.contrato.importeCargosAdicionales = contrato.importeCargosAdicionales;
      this.contrato.porcentajeCostoFinanciero = contrato.porcentajeCostoFinanciero;
      this.contrato.importeCostoFinanciero = contrato.importeCostoFinanciero;
      this.contrato.importe = contrato.importe;
      this.contrato.porcentajeIva = contrato.porcentajeIva;
      this.contrato.iva = contrato.iva;
      this.contrato.total = contrato.total;
      this.contrato.fechaInicio = contrato.fechaInicio;
      this.contrato.fechaTermino = contrato.fechaTermino;
      this.contrato.plazo = contrato.plazo;
      this.contrato.periodicidadSancion = contrato.periodicidadSancion;
      this.contrato.porcentajeSancion = contrato.porcentajeSancion;
      this.contrato.tipoRetencion = contrato.tipoRetencion || 'baseDias';
      this.contrato.porcentajeRetencion = contrato.porcentajeRetencion || 0.5;
      this.contrato.normatividad = contrato.normatividad;
      this.obras = this.normalizarObrasContrato(contrato.obras || []);
      this.seleccionarObras = this.obras.length > 0;
    },
    limpiarFormulario() {
      this.$refs.editarContrato.reset();
    },
    cerrar() {
      this.limpiarFormulario();
      this.$emit('cerrar');
    },
  },
  computed: {
    fechaInicio() {
      return this.contrato.fechaInicio;
    },
    fechaTermino() {
      return this.contrato.fechaTermino;
    },
    getLicitacionesListasParaContratar() {
      if (!this.licitacionesListasParaContratar) return [];
      return this.licitacionesListasParaContratar;
    },
    getAdjudicacionesDirectasListasParaContratar() {
      if (!this.adjudicacionesDirectasListasParaContratar) return [];
      return this.adjudicacionesDirectasListasParaContratar;
    },
    getLicitacionesSelect() {
      return this.getLicitacionesListasParaContratar.map((licitacion) => ({
        id: licitacion.id,
        texto: `${licitacion.numero} - ${licitacion.objeto}`,
      }));
    },
    getAdjudicacionesDirectasSelect() {
      return this.getAdjudicacionesDirectasListasParaContratar.map((adjudicacion) => ({
        id: adjudicacion.id,
        texto: `${adjudicacion.numeroContrato} - ${adjudicacion.meta}`,
      }));
    },
    obrasPickerSelection() {
      return this.obras.map((obra) => ({
        id: obra.idObra || obra.id,
        clave: obra.clave,
        meta: obra.meta,
      }));
    },
  },
  watch: {
    contratoEditar: {
      handler(contrato) {
        this.fetch(contrato);
        this.obra.clave = contrato.numero;
        this.obra.ejercicio = contrato.ejercicio;
        this.obra.meta = contrato.meta;
      },
    },
    contrato: {
      handler(contrato) {
        this.obra.clave = contrato.numero;
        this.obra.ejercicio = contrato.ejercicio;
        this.obra.meta = contrato.meta;
      },
      deep: true,
    },
    formularioValido: {
      handler(valor) {
        this.$emit('formularioValido', valor);
      },
    },
    enviandoFormulario: {
      handler(valor) {
        this.$emit('enviandoFormulario', valor);
      },
    },
    fechaInicio() {
      this.setPlazo();
    },
    fechaTermino() {
      this.setPlazo();
    },
    'contrato.importe': {
      handler() {
        this.calcularTotal();
      },
    },
  },
};
</script>

<style lang="scss">

</style>
